import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Calculator } from "lucide-react";
import Eyebrow from "@/components/Eyebrow";
import { sanitizePhone, validateLead } from "@/lib/validation";

const API = "/api";

const newSubmissionId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const ENTITY = [
  { id: "sro_dph", title: "Plátce DPH (s.r.o.)", desc: "Plné účetnictví, DPH, kontrolní hlášení." },
  { id: "sro_neplatce", title: "Neplátce DPH (s.r.o.)", desc: "Plné účetnictví bez režimu DPH." },
  { id: "osvc_evidence", title: "OSVČ – daňová evidence", desc: "Pro fyzické osoby s běžným režimem." },
  { id: "osvc_pausal", title: "OSVČ – paušální daň", desc: "Minimální administrativa, klidná hlava." },
];

const DOCS = [
  { id: "0-50", title: "0–50", desc: "Drobná firma / OSVČ" },
  { id: "51-150", title: "51–150", desc: "Menší s.r.o." },
  { id: "151-300", title: "151–300", desc: "Střední provoz" },
  { id: "301-500", title: "301–500", desc: "Větší firma" },
  { id: "500+", title: "500+", desc: "Vysoký objem" },
];

const EMPS = [
  { id: "0", title: "Bez zaměstnanců" },
  { id: "1-5", title: "1–5" },
  { id: "6-15", title: "6–15" },
  { id: "16+", title: "16+" },
];

const ADDONS = [
  { id: "tax_advisory", title: "Daňové poradenství", desc: "Optimalizace, konzultace, audit." },
  { id: "custom_reports", title: "Reporty na míru", desc: "Měsíční manažerské výkazy." },
  { id: "authority_rep", title: "Zastoupení na úřadech", desc: "FÚ, ČSSZ, ZP – komunikujeme za vás." },
  { id: "foreign_currency", title: "Cizí měna / zahraniční obchod", desc: "Reverse charge, Intrastat." },
  { id: "inventory", title: "Skladové hospodářství", desc: "Sklad, materiál, zásoby, výrobky." },
];

const ENTITY_BASE = { sro_dph: 4500, sro_neplatce: 2500, osvc_evidence: 1500, osvc_pausal: 800 };
const DOC_PRICE = { "0-50": 0, "51-150": 1500, "151-300": 3500, "301-500": 6000, "500+": 9000 };
const EMP_PRICE = { "0": 0, "1-5": 1500, "6-15": 4500, "16+": 8500 };
const ADDON_PRICE = { tax_advisory: 1000, custom_reports: 800, authority_rep: 600, foreign_currency: 1200, inventory: 1500 };

function formatCZK(n) {
  return n.toLocaleString("cs-CZ").replace(/,/g, " ") + " Kč";
}

export default function PricingCalculator({ id = "kalkulacka", embedded = true }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    entity_type: "",
    document_volume: "",
    employees: "",
    addons: [],
  });
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    note: "",
    gdpr_consent: false,
    website: "",
  });
  const [leadTouched, setLeadTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(newSubmissionId);

  const leadErrors = validateLead(lead);
  const setLeadField = (f, v) => setLead((d) => ({ ...d, [f]: v }));
  const markLeadTouched = (f) =>
    setLeadTouched((t) => (t[f] ? t : { ...t, [f]: true }));
  const showLeadError = (f) => leadTouched[f] && leadErrors[f];

  const price = useMemo(() => {
    if (!data.entity_type) return 0;
    return (
      (ENTITY_BASE[data.entity_type] || 0) +
      (DOC_PRICE[data.document_volume] || 0) +
      (EMP_PRICE[data.employees] || 0) +
      (data.addons || []).reduce((s, a) => s + (ADDON_PRICE[a] || 0), 0)
    );
  }, [data]);

  const total = 4; // 0..3 then result step 4
  const canNext = (() => {
    if (step === 0) return !!data.entity_type;
    if (step === 1) return !!data.document_volume;
    if (step === 2) return !!data.employees;
    if (step === 3) return true; // addons optional
    return true;
  })();

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const reset = () => {
    setStep(0);
    setData({ entity_type: "", document_volume: "", employees: "", addons: [] });
    setShowLead(false);
    setSubmitted(false);
    setSubmissionId(newSubmissionId());
  };

  const toggleAddon = (id) => {
    setData((d) => ({
      ...d,
      addons: d.addons.includes(id) ? d.addons.filter((x) => x !== id) : [...d.addons, id],
    }));
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (Object.keys(leadErrors).length) {
      setLeadTouched({
        name: true,
        email: true,
        phone: true,
        gdpr_consent: true,
      });
      toast.error(leadErrors.gdpr_consent || "Zkontrolujte prosím vyplněná pole.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiry`, {
        ...lead,
        estimated_price: price,
        params: {
          entity_type: data.entity_type,
          document_volume: data.document_volume,
          employees: data.employees,
          addons: data.addons,
        },
        submission_id: submissionId,
      });
      setSubmitted(true);
      toast.success("Děkujeme! Ozveme se vám obvykle do jednoho pracovního dne.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Příliš mnoho odeslání z této IP. Zkuste to prosím za chvíli.");
      } else {
        toast.error("Odeslání se nezdařilo. Zkuste to prosím znovu nebo nás kontaktujte přímo.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const StepLabel = ({ n, label }) => (
    <div className="flex items-center gap-3">
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border transition-all ${
          step >= n ? "bg-magenta text-white border-magenta" : "bg-white text-ink/50 border-line"
        }`}
      >
        {step > n ? <Check className="w-3.5 h-3.5" /> : n + 1}
      </div>
      <span className={`text-sm font-medium hidden md:block ${step >= n ? "text-ink" : "text-ink/50"}`}>{label}</span>
    </div>
  );

  return (
    <section
      id={id}
      data-testid="pricing-calculator"
      className={embedded ? "px-6 md:px-12 py-24 md:py-32 bg-cream" : "py-12"}
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <Eyebrow>Kalkulačka ceny</Eyebrow>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
            Spočítejte si cenu <span className="text-magenta">za pár kliknutí.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted2 leading-relaxed">
            Orientační odhad měsíční ceny účetnictví. Žádné registrace, žádné závazky — jen rychlý přehled.
          </p>
        </div>

        <div className="bg-white border border-line rounded-lg overflow-hidden">
          {/* Progress */}
          <div className="px-6 md:px-12 py-6 border-b border-line flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 md:gap-8 flex-wrap">
              <StepLabel n={0} label="Subjekt" />
              <span className="hidden md:block w-8 h-px bg-line" />
              <StepLabel n={1} label="Doklady" />
              <span className="hidden md:block w-8 h-px bg-line" />
              <StepLabel n={2} label="Mzdy" />
              <span className="hidden md:block w-8 h-px bg-line" />
              <StepLabel n={3} label="Doplňky" />
            </div>
            {step < 4 && (
              <div className="flex items-center gap-2 text-sm text-muted2">
                <Calculator className="w-4 h-4 text-magenta" />
                Krok {step + 1} z {total}
              </div>
            )}
          </div>

          <div className="px-6 md:px-12 py-10 md:py-14 min-h-[420px]">
            {step === 0 && (
              <StepGrid
                title="Jaký subjekt vedete?"
                subtitle="Vyberte typ — podle něj nastavíme rozsah služeb."
                items={ENTITY}
                value={data.entity_type}
                onChange={(v) => setData((d) => ({ ...d, entity_type: v }))}
                testId="step-entity"
              />
            )}
            {step === 1 && (
              <StepGrid
                title="Kolik dokladů měsíčně zpracováváte?"
                subtitle="Faktury přijaté i vydané, pokladna, banka — orientačně."
                items={DOCS}
                value={data.document_volume}
                onChange={(v) => setData((d) => ({ ...d, document_volume: v }))}
                testId="step-docs"
              />
            )}
            {step === 2 && (
              <StepGrid
                title="Kolik zaměstnanců máte?"
                subtitle="Mzdy, ELDP, ČSSZ, zdravotní pojišťovny — vše vyřešíme za vás."
                items={EMPS}
                value={data.employees}
                onChange={(v) => setData((d) => ({ ...d, employees: v }))}
                testId="step-emps"
              />
            )}
            {step === 3 && (
              <div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">Co dalšího potřebujete?</h3>
                <p className="text-muted2 mt-2 mb-8">Volitelné — vyberte, co se vás týká. Můžete ponechat prázdné.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {ADDONS.map((a) => {
                    const active = data.addons.includes(a.id);
                    return (
                      <button
                        type="button"
                        key={a.id}
                        data-testid={`addon-${a.id}`}
                        onClick={() => toggleAddon(a.id)}
                        className={`text-left p-5 border rounded-md transition-all ${
                          active
                            ? "border-magenta bg-magenta/5"
                            : "border-line hover:border-ink/40 bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-display text-lg md:text-xl font-semibold text-ink leading-snug">{a.title}</div>
                            <div className="text-sm text-muted2 mt-1.5">{a.desc}</div>
                          </div>
                          <div
                            className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-1 ${
                              active ? "bg-magenta border-magenta" : "border-ink/30 bg-white"
                            }`}
                          >
                            {active && <Check className="w-3.5 h-3.5 text-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="result-step">
                <div className="grid md:grid-cols-12 gap-10 items-start">
                  <div className="md:col-span-7">
                    <Eyebrow>Orientační kalkulace</Eyebrow>
                    <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                      <span className="font-display text-2xl text-ink/60 font-medium">od</span>
                      <span data-testid="estimated-price" className="font-display text-5xl md:text-7xl tracking-tighter text-ink font-medium">
                        {formatCZK(price).replace(" Kč", "")}
                      </span>
                      <span className="font-display text-2xl text-ink font-medium">Kč / měsíc</span>
                    </div>
                    <p className="mt-6 text-sm text-muted2 max-w-lg leading-relaxed">
                      Jedná se o orientační kalkulaci. Finální cena bude upřesněna po nezávazné konzultaci na základě
                      konkrétních potřeb vaší firmy.
                    </p>

                    <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                      <SummaryItem label="Subjekt" value={ENTITY.find((x) => x.id === data.entity_type)?.title} />
                      <SummaryItem label="Doklady" value={DOCS.find((x) => x.id === data.document_volume)?.title} />
                      <SummaryItem label="Mzdy" value={EMPS.find((x) => x.id === data.employees)?.title} />
                      <SummaryItem
                        label="Doplňky"
                        value={data.addons.length ? `${data.addons.length} ks` : "—"}
                      />
                    </div>

                    <div className="mt-10 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => setShowLead(true)}
                        data-testid="open-lead-form"
                        className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white px-7 py-4 rounded-full font-medium transition-colors"
                      >
                        Chci nezávaznou nabídku <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={reset}
                        data-testid="reset-calc"
                        className="inline-flex items-center gap-2 border border-ink/20 hover:border-magenta hover:text-magenta text-ink px-7 py-4 rounded-full font-medium transition-colors"
                      >
                        Spočítat znovu
                      </button>
                    </div>
                  </div>

                  <div className="md:col-span-5">
                    {showLead && !submitted && (
                      <form
                        data-testid="lead-form"
                        onSubmit={submitLead}
                        noValidate
                        className="bg-cream border border-line p-6 md:p-8 rounded-md"
                      >
                        <h4 className="font-display text-xl font-medium mb-1">Pošleme vám nabídku</h4>
                        <p className="text-sm text-muted2 mb-5">
                          Ozveme se obvykle do jednoho pracovního dne.
                        </p>
                        <input
                          type="text"
                          name="website"
                          tabIndex="-1"
                          autoComplete="off"
                          className="hidden"
                          value={lead.website}
                          onChange={(e) => setLeadField("website", e.target.value)}
                        />
                        <div className="space-y-3">
                          <Field
                            label="Jméno a příjmení *"
                            testId="lead-name"
                            autoComplete="name"
                            value={lead.name}
                            onChange={(v) => setLeadField("name", v)}
                            onBlur={() => markLeadTouched("name")}
                            error={showLeadError("name") ? leadErrors.name : null}
                          />
                          <Field
                            label="E-mail *"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            testId="lead-email"
                            value={lead.email}
                            onChange={(v) => setLeadField("email", v)}
                            onBlur={() => markLeadTouched("email")}
                            error={showLeadError("email") ? leadErrors.email : null}
                          />
                          <Field
                            label="Telefon"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            placeholder="+420 777 123 456"
                            testId="lead-phone"
                            value={lead.phone}
                            onChange={(v) => setLeadField("phone", sanitizePhone(v))}
                            onBlur={() => markLeadTouched("phone")}
                            error={showLeadError("phone") ? leadErrors.phone : null}
                          />
                          <Field
                            label="Firma"
                            testId="lead-company"
                            autoComplete="organization"
                            value={lead.company}
                            onChange={(v) => setLeadField("company", v)}
                          />
                          <div>
                            <label className="block text-xs uppercase tracking-wider text-muted2 mb-1.5">Poznámka</label>
                            <textarea
                              data-testid="lead-note"
                              rows={3}
                              value={lead.note}
                              onChange={(e) => setLeadField("note", e.target.value)}
                              className="w-full bg-white border border-line rounded-md px-3 py-2.5 text-sm focus:border-magenta focus:ring-1 focus:ring-magenta outline-none transition"
                            />
                          </div>
                          <div>
                            <label className="flex items-start gap-3 text-sm text-ink/80 cursor-pointer">
                              <input
                                type="checkbox"
                                data-testid="lead-gdpr"
                                checked={lead.gdpr_consent}
                                onChange={(e) => {
                                  setLeadField("gdpr_consent", e.target.checked);
                                  markLeadTouched("gdpr_consent");
                                }}
                                className="mt-1 accent-magenta w-4 h-4"
                              />
                              <span>
                                Souhlasím se{" "}
                                <a href="/zasady-zpracovani-osobnich-udaju" target="_blank" rel="noreferrer" className="text-magenta hover:underline">
                                  zpracováním osobních údajů
                                </a>{" "}
                                za účelem vyřízení poptávky.
                              </span>
                            </label>
                            {showLeadError("gdpr_consent") && (
                              <p className="mt-1.5 text-xs text-red-600">{leadErrors.gdpr_consent}</p>
                            )}
                          </div>
                          <button
                            type="submit"
                            disabled={submitting}
                            data-testid="lead-submit"
                            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-ink2 hover:bg-magenta text-white px-6 py-3.5 rounded-full font-medium transition-colors disabled:opacity-60"
                          >
                            {submitting ? "Odesíláme…" : "Odeslat poptávku"}
                          </button>
                        </div>
                      </form>
                    )}
                    {submitted && (
                      <div data-testid="lead-success" className="bg-cream border border-line p-8 rounded-md">
                        <div className="w-12 h-12 rounded-full bg-magenta/10 flex items-center justify-center mb-5">
                          <Check className="w-6 h-6 text-magenta" />
                        </div>
                        <h4 className="font-display text-xl font-medium">Děkujeme!</h4>
                        <p className="text-sm text-muted2 mt-2 leading-relaxed">
                          Vaše poptávka dorazila. Ozveme se obvykle do jednoho pracovního dne.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {step < 4 && (
            <div className="px-6 md:px-12 py-6 border-t border-line flex items-center justify-between">
              <button
                type="button"
                onClick={back}
                disabled={step === 0}
                data-testid="calc-back"
                className="inline-flex items-center gap-2 text-sm font-medium text-ink/70 hover:text-magenta disabled:opacity-30 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Zpět
              </button>
              <button
                type="button"
                onClick={next}
                disabled={!canNext}
                data-testid="calc-next"
                className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark disabled:bg-ink/20 disabled:cursor-not-allowed text-white px-6 py-3 rounded-full text-sm font-medium transition-colors"
              >
                {step === 3 ? "Zobrazit cenu" : "Pokračovat"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StepGrid({ title, subtitle, items, value, onChange, testId }) {
  return (
    <div data-testid={testId}>
      <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">{title}</h3>
      {subtitle && <p className="text-muted2 mt-2 mb-8">{subtitle}</p>}
      <div className={`grid gap-4 ${items.length > 4 ? "sm:grid-cols-2 md:grid-cols-3" : "sm:grid-cols-2"}`}>
        {items.map((it) => {
          const active = value === it.id;
          return (
            <button
              type="button"
              key={it.id}
              data-testid={`opt-${it.id}`}
              onClick={() => onChange(it.id)}
              className={`text-left p-5 border rounded-md transition-all ${
                active
                  ? "border-magenta bg-magenta/5"
                  : "border-line hover:border-ink/40 bg-white"
              }`}
            >
              <div className="font-display text-lg md:text-xl font-semibold text-ink leading-snug">{it.title}</div>
              {it.desc && <div className="text-sm text-muted2 mt-1.5">{it.desc}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted2 mb-1">{label}</div>
      <div className="text-sm font-medium text-ink">{value || "—"}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  onBlur,
  testId,
  type = "text",
  inputMode,
  autoComplete,
  placeholder,
  error,
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-muted2 mb-1.5">{label}</label>
      <input
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
        className={`w-full bg-white border rounded-md px-3 py-2.5 text-sm focus:ring-1 outline-none transition ${
          error
            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
            : "border-line focus:border-magenta focus:ring-magenta"
        }`}
      />
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}
